// Minimal per-route SEO head-tag helper. No dependency, no SSR.
// Google/Bing render JS and DO pick up head tags mutated on the client, so
// this is sufficient to give every public MESSAGE its own title, description,
// canonical, OG and Twitter card. Reverts to homepage tags on unmount so
// SPA navigation doesn't leak MESSAGE-specific tags into other routes.
import { useEffect } from "react";

const DEFAULTS = {
  title: "VOIHU — Let your voice travel.",
  description:
    "VOIHU is a global movement where one human, one voice, becomes the voice of humanity. Start a MESSAGE, add your VOICE, let it travel the world.",
  canonical: "https://voihuworld.com/",
  ogImage: "https://voihuworld.com/voihu-primary.webp",
  ogType: "website",
  twitterCard: "summary_large_image",
};

function upsertMeta({ name, property, content }) {
  if (!content) return null;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, created, prev };
}

function upsertLink({ rel, href }) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, created, prev };
}

function truncate(str, max) {
  if (!str) return "";
  const clean = String(str).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1).trimEnd() + "…";
}

/**
 * Apply per-page SEO tags. Pass a stable `deps` array (like React hook deps)
 * so the effect re-runs when the page's data changes.
 *
 *   useSeoTags({ title, description, canonical, ogImage }, [id, title]);
 */
export function useSeoTags(seo, deps = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Caller may explicitly pass `null` / `undefined` while its own data
    // is still loading (see Message.jsx). In that case we deliberately
    // DO NOT touch <head> — the site-wide fallback metadata baked into
    // `index.html` (or the previous route's tags on client-side
    // navigation) must remain the source of truth for any crawler that
    // snapshots the page before the async data settles. Without this
    // guard the first-render path would write partial fields
    // (og:type=article + generic-VOIHU og:title), which is precisely
    // the misleading state social preview crawlers were catching.
    if (!seo) return;
    const title = seo?.title || DEFAULTS.title;
    const description = truncate(seo?.description || DEFAULTS.description, 300);
    const canonical = seo?.canonical || DEFAULTS.canonical;
    const ogImage = seo?.ogImage || DEFAULTS.ogImage;
    const ogType = seo?.ogType || DEFAULTS.ogType;
    const twitterCard = seo?.twitterCard || DEFAULTS.twitterCard;

    const prevTitle = document.title;
    document.title = title;

    const mutations = [
      upsertMeta({ name: "description", content: description }),
      upsertMeta({ property: "og:title", content: title }),
      upsertMeta({ property: "og:description", content: description }),
      upsertMeta({ property: "og:url", content: canonical }),
      upsertMeta({ property: "og:type", content: ogType }),
      upsertMeta({ property: "og:image", content: ogImage }),
      upsertMeta({ name: "twitter:title", content: title }),
      upsertMeta({ name: "twitter:description", content: description }),
      upsertMeta({ name: "twitter:image", content: ogImage }),
      upsertMeta({ name: "twitter:card", content: twitterCard }),
      upsertLink({ rel: "canonical", href: canonical }),
    ].filter(Boolean);

    return () => {
      // Restore prior values so SPA navigation to another page doesn't leak
      // this page's tags. Newly-created nodes are removed.
      document.title = prevTitle;
      for (const m of mutations) {
        if (!m) continue;
        if (m.created) {
          m.el.remove();
        } else if (m.prev !== null && m.prev !== undefined) {
          const attr = m.el.tagName === "LINK" ? "href" : "content";
          m.el.setAttribute(attr, m.prev);
        }
      }
    };
    // deps are provided by caller
  }, deps);
}

export const SEO_DEFAULTS = DEFAULTS;
